import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useFavorites } from '../../hooks/api/useFavorites';
import { useSettingsModal } from '../../contexts/settings-modal-context';
import { PageHeader } from '../../components/modals/settings-modal/page-header';
import { AudioExperienceTile, AudioExperienceTileSkeleton } from '../../components/audio-experience-tile';
import { VideoExperienceTile } from '../../components/video-experience-tile';
import { ResourceTile, ResourceTileSkeleton } from '../../components/resource-tile';
import { PeerListenerTile, PeerListenerTileSkeleton } from '../../components/peer-listener-tile';
import { ArrowButton } from '../../assets/images/svgs';
import { Content, Resource, People } from '../../hooks/api/useExperiences';
import { InlineSpinner } from '../../components/common/spinner';

const ArrowButtonWithHover: React.FC<{
  onClick: () => void;
  disabled: boolean;
  direction: 'left' | 'right';
  ariaLabel: string;
}> = ({ onClick, disabled, direction, ariaLabel }) => {
  const [isHovered, setIsHovered] = useState(false);

  const backgroundColor = disabled
    ? 'rgb(var(--color-gray-light))'
    : isHovered
      ? 'rgb(var(--color-button-secondary-hover))'
      : 'rgb(var(--color-button-secondary))';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex h-6 w-6 flex-shrink-0 items-center justify-center transition-colors ${
        disabled ? 'cursor-default' : 'cursor-pointer'
      }`}
      style={disabled ? { cursor: 'default' } : undefined}
      aria-label={ariaLabel}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ArrowButton backgroundColor={backgroundColor} disabled={disabled} direction={direction} className="h-6 w-6" />
    </button>
  );
};

interface HorizontalScrollSectionProps {
  title: string;
  items: any[];
  isLoading: boolean;
  renderItem: (item: any, index: number) => React.ReactNode;
  renderSkeleton: () => React.ReactNode;
  emptyMessage?: string;
}

const HorizontalScrollSection: React.FC<HorizontalScrollSectionProps> = ({
  title,
  items,
  isLoading,
  renderItem,
  renderSkeleton,
  emptyMessage = 'No items found',
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const getCarouselItems = (): HTMLElement[] => {
    if (!carouselRef.current) return [];
    return Array.from(carouselRef.current.querySelectorAll('[data-carousel-item]')) as HTMLElement[];
  };

  const getViewportDimensions = () => {
    if (!carouselRef.current) return null;
    const carousel = carouselRef.current;
    return {
      scrollLeft: carousel.scrollLeft,
      width: carousel.clientWidth,
      right: carousel.scrollLeft + carousel.clientWidth,
    };
  };

  const findFirstVisibleItem = (items: HTMLElement[], viewportLeft: number, viewportRight: number): number => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      if (itemLeft < viewportRight && itemRight > viewportLeft) {
        return i;
      }
    }
    return -1;
  };

  const findRightmostVisibleItem = (
    items: HTMLElement[],
    viewportLeft: number,
    viewportRight: number,
  ): { item: HTMLElement; index: number } | null => {
    let rightmostItem: HTMLElement | null = null;
    let rightmostIndex = -1;
    let rightmostRight = viewportLeft;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      if (itemLeft < viewportRight && itemRight > viewportLeft) {
        if (itemRight > rightmostRight) {
          rightmostItem = item;
          rightmostIndex = i;
          rightmostRight = itemRight;
        }
      }
    }

    return rightmostItem ? { item: rightmostItem, index: rightmostIndex } : null;
  };

  useEffect(() => {
    const updateScrollButtons = () => {
      if (!carouselRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };

    updateScrollButtons();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        carousel.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [items.length]);

  const scrollLeft = () => {
    const items = getCarouselItems();
    const viewport = getViewportDimensions();
    if (!items.length || !viewport || !carouselRef.current) return;

    const firstVisibleIndex = findFirstVisibleItem(items, viewport.scrollLeft, viewport.right);

    if (firstVisibleIndex > 0) {
      carouselRef.current.scrollTo({ left: items[firstVisibleIndex - 1].offsetLeft, behavior: 'smooth' });
    } else if (firstVisibleIndex === 0) {
      carouselRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const items = getCarouselItems();
    const viewport = getViewportDimensions();
    if (!items.length || !viewport || !carouselRef.current) return;

    const GAP = 24;
    const rightmost = findRightmostVisibleItem(items, viewport.scrollLeft, viewport.right);

    if (!rightmost || rightmost.index >= items.length - 1) {
      if (rightmost?.index === items.length - 1) {
        const lastItem = items[items.length - 1];
        const maxScroll = lastItem.offsetLeft + lastItem.offsetWidth - viewport.width;
        if (carouselRef.current.scrollLeft < maxScroll) {
          carouselRef.current.scrollTo({ left: Math.max(0, maxScroll), behavior: 'smooth' });
        }
      }
      return;
    }

    const nextItem = items[rightmost.index + 1];
    const isItemFullyVisible = (item: HTMLElement, viewportLeft: number, viewportRight: number): boolean => {
      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      return itemLeft >= viewportLeft && itemRight <= viewportRight;
    };

    if (isItemFullyVisible(nextItem, viewport.scrollLeft, viewport.right)) {
      for (let i = rightmost.index + 2; i < items.length; i++) {
        const item = items[i];
        if (!isItemFullyVisible(item, viewport.scrollLeft, viewport.right)) {
          carouselRef.current.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
          return;
        }
      }
      const lastItem = items[items.length - 1];
      const maxScroll = lastItem.offsetLeft + lastItem.offsetWidth - viewport.width;
      carouselRef.current.scrollTo({ left: Math.max(0, maxScroll), behavior: 'smooth' });
    } else {
      carouselRef.current.scrollBy({ left: rightmost.item.offsetWidth + GAP, behavior: 'smooth' });
    }
  };

  if (isLoading) {
    return (
      <div className="mb-12">
        <h2 className="font-albert-sans mb-4 text-[28px] font-semibold leading-7 text-black">{title}</h2>
        <div className="relative">
          <div
            ref={carouselRef}
            className="flex gap-6 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={`skeleton-${index}`} data-carousel-item className="flex-shrink-0">
                {renderSkeleton()}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mb-12">
        <h2 className="font-albert-sans mb-4 text-[28px] font-semibold leading-7 text-black">{title}</h2>
        <p className="font-open-sans text-section-header-slate text-base">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="mb-12">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-albert-sans text-[28px] font-semibold leading-7 text-black">{title}</h2>
        <div className="flex items-center gap-2">
          <ArrowButtonWithHover
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            direction="left"
            ariaLabel="Scroll left"
          />
          <ArrowButtonWithHover
            onClick={scrollRight}
            disabled={!canScrollRight}
            direction="right"
            ariaLabel="Scroll right"
          />
        </div>
      </div>
      <div className="relative">
        <div
          ref={carouselRef}
          className="flex gap-6 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((item, index) => (
            <div key={item.id || index} data-carousel-item className="flex-shrink-0">
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MyFavorites: React.FC = () => {
  const { data: favoritesData, isLoading } = useFavorites();
  const { closeModal } = useSettingsModal();

  const listenerRoles = useMemo(() => favoritesData?.data?.listener_roles || [], [favoritesData]);
  const resources = useMemo(
    () => [...(favoritesData?.data?.client_resources || []), ...(favoritesData?.data?.global_resources || [])],
    [favoritesData],
  );
  const content = useMemo(() => favoritesData?.data?.listener_audio || [], [favoritesData]);

  const handlePeerClick = useCallback(() => {
    closeModal();
  }, [closeModal]);

  if (isLoading && !favoritesData) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <InlineSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col p-6">
      <PageHeader
        header="My Favorites"
        subHeader="View and manage all your favorite peer listeners, resources, and content."
      />

      <HorizontalScrollSection
        title="Peer Listeners"
        items={listenerRoles}
        isLoading={isLoading}
        renderItem={(peer: People) => <PeerListenerTile peer={peer} onTileClick={handlePeerClick} hideScheduleButton />}
        renderSkeleton={() => <PeerListenerTileSkeleton />}
        emptyMessage="You haven't favorited any peer listeners yet."
      />

      <HorizontalScrollSection
        title="Resources"
        items={resources}
        isLoading={isLoading}
        renderItem={(resource: Resource) => (
          <ResourceTile
            id={resource.id}
            resource={resource}
            image={resource.image_url}
            resourceName={resource.name}
            caption={resource.description}
            tagGroupNames={(resource as any).tag_group_names}
            isGlobal={!resource.client_id}
          />
        )}
        renderSkeleton={() => <ResourceTileSkeleton />}
        emptyMessage="You haven't favorited any resources yet."
      />

      <HorizontalScrollSection
        title="Content"
        items={content}
        isLoading={isLoading}
        renderItem={(item: Content) => {
          if (item.video_id) {
            return <VideoExperienceTile experience={item} />;
          }
          return <AudioExperienceTile experience={item} />;
        }}
        renderSkeleton={() => <AudioExperienceTileSkeleton />}
        emptyMessage="You haven't favorited any content yet."
      />
    </div>
  );
};
